// Modale « Mettre à jour RouterOS » : immédiate ou planifiée, avec fenêtre de
// maintenance. La plateforme enchaîne elle-même les étapes (sauvegarde préalable,
// téléchargement, redémarrage, vérification, firmware RouterBOARD) : le routeur
// est coupé pendant le ou les redémarrages, d'où la phrase de confirmation pour
// l'exécution immédiate — c'est le réseau d'un client.

import { useState } from 'react';
import { X, ArrowUpCircle, AlertTriangle, Clock, ExternalLink } from 'lucide-react';
import { mikrotikCall, defaultTonight } from '../lib/mikrotik.jsx';

const CONFIRM_PHRASE = 'METTRE À JOUR';
const CHANGELOG_URL = 'https://mikrotik.com/download/changelogs';

export function MikrotikUpgradeModal({ device, label, onClose }) {
  const hasOs = !!device.updateAvailable;
  const hasFirmware = !!device.firmwareUpgradeAvailable;
  const [mode, setMode] = useState('schedule'); // 'now' | 'schedule'
  const [whenLocal, setWhenLocal] = useState(defaultTonight);
  const [maintenance, setMaintenance] = useState(true);
  const [includeFirmware, setIncludeFirmware] = useState(true);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const whenInvalid = mode === 'schedule' && (!whenLocal || new Date(whenLocal) <= new Date());
  const confirmOk = mode !== 'now' || confirmText.trim().toUpperCase() === CONFIRM_PHRASE;
  const withFirmware = hasFirmware && includeFirmware;

  const submit = async () => {
    if (whenInvalid || !confirmOk) return;
    setSaving(true);
    setError(null);
    const { ok, json } = await mikrotikCall('/platform/mikrotik/' + device.agentId + '/upgrade', 'POST', {
      // Champ en heure LOCALE ; le serveur raisonne en UTC.
      scheduledAt: mode === 'schedule' ? new Date(whenLocal).toISOString() : null,
      maintenanceMinutes: maintenance ? 30 : 0,
      includeFirmware: withFirmware,
      reason: reason.trim() || null,
    });
    setSaving(false);
    if (ok) onClose(true, json?.message || null);
    else setError(json?.error || 'Échec de la demande de mise à jour.');
  };

  const steps = [
    'Sauvegarde préalable (export + binaire)',
    hasOs ? `Téléchargement de RouterOS ${device.latestVersion || ''}`.trim() : 'Téléchargement de la mise à jour',
    'Redémarrage du routeur',
    'Vérification de la version installée',
    ...(withFirmware ? ['Mise à jour du firmware RouterBOARD et second redémarrage'] : []),
  ];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] p-4">
      <div className="bg-card border border-rule rounded-2xl w-[520px] max-w-full max-h-[92vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 pt-6 pb-3 flex-none">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ArrowUpCircle size={18} strokeWidth={1.75} className="text-blue-400" />
              <h2 className="text-base font-bold text-fg">Mettre à jour le routeur</h2>
            </div>
            <button
              onClick={() => onClose(false)}
              className="bg-transparent border-none text-muted cursor-pointer hover:text-fg p-1"
              aria-label="Fermer"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
          <div className="text-[12px] text-muted-2">
            Routeur : <span className="text-fg-2 font-semibold">{label}</span>
          </div>
        </div>

        <div className="px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <div className="bg-sidebar border border-rule rounded-xl px-3.5 py-2.5 mb-3 text-[12.5px] text-fg-2">
            {hasOs ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-2">RouterOS</span>
                <span className="font-mono">{device.routerOsVersion || '?'}</span>
                <span className="text-muted-2">{'→'}</span>
                <span className="font-mono font-semibold text-fg">{device.latestVersion}</span>
                <span className="text-[11px] text-muted-2">(canal {device.updateChannel || '?'})</span>
              </div>
            ) : (
              <div className="text-muted">RouterOS est à jour ({device.routerOsVersion || '?'}).</div>
            )}
            {hasFirmware && (
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <span className="text-muted-2">Firmware</span>
                <span className="font-mono">{device.currentFirmware || '?'}</span>
                <span className="text-muted-2">{'→'}</span>
                <span className="font-mono font-semibold text-fg">{device.upgradeFirmware}</span>
              </div>
            )}
            <a
              href={CHANGELOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-[11.5px] text-blue-400 hover:underline"
            >
              Notes de version <ExternalLink size={11} strokeWidth={1.75} />
            </a>
          </div>

          <div className="text-[11px] text-muted-2 uppercase font-medium mb-1.5">Étapes enchaînées</div>
          <ol className="list-decimal list-inside text-[12px] text-fg-2 mb-4 space-y-0.5">
            {steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>

          <div className="bg-warn/10 border border-warn/30 rounded-xl px-3.5 py-2.5 mb-4 text-[12px] text-fg-2 flex gap-2">
            <AlertTriangle size={14} strokeWidth={1.75} className="text-warn flex-shrink-0 mt-0.5" />
            <span>
              Le routeur sera injoignable pendant le redémarrage{withFirmware ? ' (deux fois avec le firmware)' : ''} :
              le réseau qu'il dessert est coupé quelques minutes, davantage sur un petit modèle.
            </span>
          </div>

          <div className="flex gap-2 mb-3">
            {[
              ['schedule', 'Planifier'],
              ['now', 'Maintenant'],
            ].map(([k, l]) => (
              <button
                key={k}
                type="button"
                onClick={() => setMode(k)}
                className={`rounded-lg px-3 py-1.5 text-[12px] cursor-pointer border ${
                  mode === k
                    ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold'
                    : 'bg-card border-rule-2 text-muted hover:text-fg-2'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          {mode === 'schedule' && (
            <div className="mb-3">
              <label className="block text-[11px] text-muted-2 mb-1 inline-flex items-center gap-1">
                <Clock size={11} strokeWidth={1.75} /> Date et heure (heure locale)
              </label>
              <input
                type="datetime-local"
                value={whenLocal}
                onChange={(e) => setWhenLocal(e.target.value)}
                className="bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none"
              />
              {whenInvalid && <div className="text-[11px] text-crit mt-1">L'échéance doit être dans le futur.</div>}
            </div>
          )}

          <label className="flex items-center gap-2 text-[12px] text-fg-2 cursor-pointer mb-1.5">
            <input type="checkbox" checked={maintenance} onChange={(e) => setMaintenance(e.target.checked)} />
            Poser une fenêtre de maintenance de 30 min (évite l'alerte « hors ligne »)
          </label>
          {hasFirmware && (
            <label className="flex items-center gap-2 text-[12px] text-fg-2 cursor-pointer mb-1.5">
              <input type="checkbox" checked={includeFirmware} onChange={(e) => setIncludeFirmware(e.target.checked)} />
              Inclure le firmware RouterBOARD ({device.currentFirmware} {'→'} {device.upgradeFirmware})
            </label>
          )}

          <div className="mt-3">
            <label className="block text-[11px] text-muted-2 mb-1">Motif (facultatif)</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none"
            />
          </div>

          {mode === 'now' && (
            <div className="mt-3">
              <label className="block text-[11px] text-muted-2 mb-1">
                Pour lancer maintenant, saisir <span className="font-mono text-fg-2">{CONFIRM_PHRASE}</span>
              </label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full bg-sidebar border border-rule rounded-lg px-3 py-2 text-fg-2 text-xs outline-none"
              />
            </div>
          )}

          {error && (
            <div className="mt-3 bg-crit/10 border border-crit/30 rounded-xl px-3.5 py-2.5 text-[12px] text-fg-2 flex gap-2">
              <AlertTriangle size={14} strokeWidth={1.75} className="text-crit flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2.5 mt-5">
            <button
              onClick={() => onClose(false)}
              className="bg-rule border border-rule-3 rounded-lg px-4 py-2 text-muted cursor-pointer text-[13px] font-semibold"
            >
              Annuler
            </button>
            <button
              onClick={submit}
              disabled={saving || whenInvalid || !confirmOk || (!hasOs && !hasFirmware)}
              className="bg-blue-500 border-none rounded-lg px-4 py-2 text-white cursor-pointer text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
            >
              {saving ? 'Envoi…' : mode === 'now' ? 'Mettre à jour maintenant' : 'Planifier la mise à jour'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
